import React, { useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import awsmobile from '../aws-exports';
import { get, ApiError } from 'aws-amplify/api';
import { Link } from 'react-router-dom';

import './DataList.css';

Amplify.configure(awsmobile);

// Function to capitalize the first letter of each word
function capitalize(str) {
    return str
        .toLowerCase()
        .replace(/\b\w/g, char => char.toUpperCase());
}

function toSeconds(timeStr) {
    const [h = 0, m = 0, s = 0] = String(timeStr || '0:0:0').split(':').map(Number);
    return (h * 3600) + (m * 60) + s;
}

// Hours logged is a VOLUME, not a verdict. The old ramp painted "very low" red
// and "very high" green, which inverted the meaning of red one page away from
// the severity ranking. This is a single-hue intensity scale with no good/bad
// implication; the written category carries the meaning.
function volumeFor(category) {
    switch (String(category || '').toLowerCase()) {
        case 'very high':
        case 'high':
            return { color: 'var(--vol-high)', bg: 'var(--accent-subtle)' };
        case 'normal':
            return { color: 'var(--vol-mid)', bg: 'var(--sev-normal-bg)' };
        default: // low / very low
            return { color: 'var(--vol-low)', bg: 'var(--sev-normal-bg)' };
    }
}

const TimeTrackList = () => {
    const [times, setTimes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        async function fetchTimes() {
            try {
                const restOperation = get({
                    apiName: 'times', // Replace 'timesApi' with your actual API name
                    path: '/times'
                });
                const { body } = await restOperation.response;
                const jsonResponse = await body.json();

                // Assuming the response is an array, not an object with a `data` field
                setTimes(jsonResponse);
                setLoading(false);
            } catch (e) {
                console.log('GET call failed:', e);
                if (e instanceof ApiError && e.response) {
                    const errorResponse = JSON.parse(e.response.body);
                    console.error('Error response:', errorResponse);
                    setError(`Error: ${errorResponse.message}`);
                } else {
                    setError('Failed to fetch times');
                }
                setLoading(false);
            }
        }

        fetchTimes();
    }, []);

    const mostLogged = times.reduce((max, t) => Math.max(max, toSeconds(t.worked_time)), 0);

    const header = (
        <div className="page-header">
            <h2 className="page-title">Worked Time</h2>
            <p className="page-subtitle">
                Time tracked per person in ClickUp this week, with the share spent on bugs.
            </p>
        </div>
    );

    if (loading) return <>{header}<p className="loading-state" role="status">Loading tracked time…</p></>;
    if (error) return <>{header}<p className="error-state" role="alert">{error}</p></>;
    if (!times.length) {
        return <>{header}<p className="empty-state">No time has been tracked this week.</p></>;
    }

    return (
        <div className="times-container">
            {header}
            <ul className="data-list">
                {times.map((time) => {
                    const vol = volumeFor(time.hours_category);
                    const secs = toSeconds(time.worked_time);
                    return (
                        <li key={time.id}>
                            <Link to={`/times/${time.id}`} className="data-row">
                                <div className="data-row-top">
                                    <span className="data-name">{time.name}</span>
                                    <span className="data-tag" style={{ color: vol.color, background: vol.bg }}>
                                        {capitalize(time.hours_category)}
                                    </span>
                                    <span className="data-metric">{time.worked_time}</span>
                                </div>
                                <div className="data-sub">
                                    <span className="data-sub-label">Bug time</span>
                                    <span className="data-sub-value">{time.type_bug_time}</span>
                                    <span className="data-sub-label">
                                        ({time.type_bug_time_percentage} of tracked time)
                                    </span>
                                </div>
                                <div className="data-bar-track">
                                    <div
                                        className="data-bar-fill"
                                        style={{
                                            width: mostLogged ? `${(secs / mostLogged) * 100}%` : '0%',
                                            background: vol.color,
                                        }}
                                    />
                                </div>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default TimeTrackList;
